import { useState, useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Loader2, FileDown, ArrowLeft, Search, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { LOGO_BASE64 } from "@/lib/logo";

const HIGHLIGHT_BG = "#C2410C";

function getMargins(cost, price) {
  if (!cost || !price || cost <= 0 || price <= 0) return { mu: "—", md: "—" };
  const mu = (((price - cost) / cost) * 100).toFixed(2);
  const md = (((price - cost) / price) * 100).toFixed(2);
  return { mu, md };
}

function WeaverMultiSelect({ weavers, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = weavers.filter((w) => (w.label || w.name).toLowerCase().includes(search.toLowerCase()));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div ref={ref} className="relative" data-testid="weaver-multi-select">
      <div
        className="border border-stone-200 rounded-md px-3 py-2 min-h-[44px] cursor-pointer flex items-center gap-2 flex-wrap bg-white"
        onClick={() => setOpen(!open)}
      >
        {selected.length === 0 && <span className="text-stone-400 text-sm">Select suppliers...</span>}
        {selected.map((id) => {
          const w = weavers.find((x) => x.id === id);
          return w ? (
            <span key={id} className="bg-orange-100 text-[#C2410C] text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              {w.label || w.name}
              <X className="h-3 w-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); toggle(id); }} />
            </span>
          ) : null;
        })}
        <ChevronDown className="h-4 w-4 text-stone-400 ml-auto shrink-0" />
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-stone-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
              <input
                className="w-full pl-7 pr-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-1 focus:ring-[#C2410C]"
                placeholder="Search suppliers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-44">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-stone-400 text-center">No suppliers found</div>
            ) : filtered.map((w) => (
              <label key={w.id} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer text-sm">
                <input type="checkbox" checked={selected.includes(w.id)} onChange={() => toggle(w.id)} className="accent-[#C2410C]" />
                {w.label || w.name}
              </label>
            ))}
          </div>
          {weavers.length > 3 && (
            <div className="border-t border-stone-100 px-3 py-1.5 flex justify-between">
              <button className="text-xs text-[#C2410C] hover:underline" onClick={() => onChange(weavers.map((w) => w.id))}>Select All</button>
              <button className="text-xs text-stone-500 hover:underline" onClick={() => onChange([])}>Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SpecsBlock({ design }) {
  const fd = design.form_data || {};
  const rd = design.result_data || {};
  const fabricReed = parseFloat(fd.reed) + 4;
  const fabricPick = parseFloat(fd.pick) - 2;
  const specs = [
    ["Fabric Width", fd.fabric_width], ["Border Width", fd.border_width],
    ["Loom Reed", fd.reed], ["Loom Pick", fd.pick],
    ["Fabric Reed", fabricReed], ["Fabric Pick", fabricPick],
    ["Pick Rate", fd.pick_rate], ["Size", `${fd.size}m`],
  ];
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-4" data-testid="weaver-compare-specs">
      <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-3">Primary Specifications</p>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {specs.map(([k, v]) => <div key={k}><span className="text-stone-500">{k}:</span> <b>{v}</b></div>)}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mt-2">
        <div><span className="text-stone-500">Border:</span> <b>{fd.border_type}</b></div>
        <div><span className="text-stone-500">Bobbin:</span> <b>{fd.bobbin_type}</b></div>
        <div><span className="text-stone-500">Processing:</span> <b>{fd.processing_type}</b></div>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mt-2">
        <div><span className="text-stone-500">Warp:</span> <b>{fd.warp_count}/{fd.warp_quality}</b></div>
        <div><span className="text-stone-500">Weft:</span> <b>{fd.weft_count}/{fd.weft_quality}</b></div>
        <div><span className="text-stone-500">Kuri:</span> <b>{fd.kuri_count}/{fd.kuri_quality}</b></div>
      </div>
    </div>
  );
}

export default function WeaverComparePage() {
  const [designs, setDesigns] = useState([]);
  const [weavers, setWeavers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [designSearch, setDesignSearch] = useState("");
  const [selectedDesign, setSelectedDesign] = useState("");
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [warehousePct, setWarehousePct] = useState(5);

  // Combine weavers + vendors into unified supplier list
  const suppliers = [
    ...weavers.map((w) => ({ ...w, type: "weaver", label: `${w.name} (Weaver)` })),
    ...vendors.map((v) => ({ ...v, type: "vendor", label: `${v.name} (Vendor)` })),
  ];

  useEffect(() => {
    Promise.all([api.get("/saved-designs"), api.get("/weavers"), api.get("/vendors"), api.get("/settings")]).then(([dRes, wRes, vRes, sRes]) => {
      setDesigns(dRes.data);
      setWeavers(wRes.data);
      setVendors(vRes.data);
      const wh = (sRes.data || []).find((x) => x.key === "warehouse_expenses");
      if (wh && wh.value !== undefined && wh.value !== null) setWarehousePct(wh.value);
    }).catch((err) => toast.error(formatApiError(err))).finally(() => setInitLoading(false));
  }, []);

  const runComparison = async () => {
    if (!selectedDesign || selectedSuppliers.length === 0) return;
    const design = designs.find((d) => d.id === selectedDesign);
    if (!design) return;
    setLoading(true);
    try {
      const weaverIds = selectedSuppliers.filter((sid) => suppliers.find((s) => s.id === sid)?.type === "weaver").map((sid) => sid);
      const vendorIds = selectedSuppliers.filter((sid) => suppliers.find((s) => s.id === sid)?.type === "vendor").map((sid) => sid);

      const calls = [];
      if (weaverIds.length > 0) calls.push(api.post("/weaver-compare", { form_data: design.form_data, weaver_ids: weaverIds }));
      else calls.push(Promise.resolve({ data: [] }));
      if (vendorIds.length > 0) calls.push(api.post("/vendor-compare", { design_id: design.id, vendor_ids: vendorIds }));
      else calls.push(Promise.resolve({ data: { comparisons: [] } }));

      const [weaverRes, vendorRes] = await Promise.all(calls);
      const weaverComps = (weaverRes.data || []).map((c) => ({ ...c, supplier_type: "weaver" }));
      const vendorComps = (vendorRes.data?.comparisons || []).map((c) => ({ ...c, supplier_name: c.vendor_name, supplier_type: "vendor" }));

      // Normalize: weavers have result_data, vendors have flat fields
      const allComps = [
        ...weaverComps.map((c) => ({ name: c.weaver_name, type: "weaver", data: c.result_data || {}, error: c.error })),
        ...vendorComps.map((c) => ({ name: c.vendor_name || c.supplier_name, type: "vendor", data: c, error: c.error })),
      ];

      setResults({ design, comparisons: allComps });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const exportPdf = () => {
    if (!results) return;
    const { design, comparisons } = results;
    const fd = design.form_data || {};
    const rd = design.result_data || {};
    const cols = comparisons.filter((c) => !c.error);
    const fabricReed = parseFloat(fd.reed) + 4;
    const fabricPick = parseFloat(fd.pick) - 2;

    const headerCells = cols.map((c) => `<th style="padding:6px 8px;text-align:center;font-weight:700;font-size:11px;border-bottom:2px solid #E7E5E4;">${c.name}</th>`).join("");

    const row = (label, getter, highlight) => {
      const cells = cols.map((c) => {
        const val = getter(c.data || {});
        const st = highlight
          ? `background:${HIGHLIGHT_BG};color:white;font-weight:700;padding:6px 8px;text-align:right;font-family:monospace;font-size:11px;`
          : `padding:5px 8px;text-align:right;font-family:monospace;font-size:11px;`;
        return `<td style="${st}">${val}</td>`;
      }).join("");
      const ls = highlight
        ? `background:${HIGHLIGHT_BG};color:white;font-weight:700;padding:6px 8px;font-size:11px;`
        : `padding:5px 8px;color:#57534E;font-size:11px;`;
      return `<tr><td style="${ls}">${label}</td>${cells}</tr>`;
    };

    const normalRow = (label, getter) => row(label, getter, false);
    const hlRow = (label, getter) => row(label, getter, true);

    // WSP/MRP from saved design
    const wspPrice = rd.wsp_price;
    const mrpPrice = rd.mrp_price;

    const html = `
    <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:900px;color:#1C1917;">
      <div style="text-align:center;margin-bottom:8px;padding:6px 0;">
        <img src="${LOGO_BASE64}" style="height:38px;margin:0 auto;" />
        <div style="font-size:12px;color:#78716c;margin-top:3px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">Supplier Comparison</div>
        <div style="font-size:13px;font-weight:700;margin-top:4px;">${design.design_name}</div>
      </div>
      <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:6px;padding:8px 12px;margin-bottom:8px;font-size:10px;">
        <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:4px;">Primary Specifications</div>
        <div style="display:flex;flex-wrap:wrap;gap:2px 14px;">
          <div><span style="color:#78716c;">Fabric Width:</span> <b>${fd.fabric_width}</b></div>
          <div><span style="color:#78716c;">Border Width:</span> <b>${fd.border_width}</b></div>
          <div><span style="color:#78716c;">Loom Reed:</span> <b>${fd.reed}</b></div>
          <div><span style="color:#78716c;">Loom Pick:</span> <b>${fd.pick}</b></div>
          <div><span style="color:#78716c;">Fabric Reed:</span> <b>${fabricReed}</b></div>
          <div><span style="color:#78716c;">Fabric Pick:</span> <b>${fabricPick}</b></div>
          <div><span style="color:#78716c;">Pick Rate:</span> <b>${fd.pick_rate}</b></div>
          <div><span style="color:#78716c;">Size:</span> <b>${fd.size}m</b></div>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:2px 14px;margin-top:3px;">
          <div><span style="color:#78716c;">Border:</span> <b>${fd.border_type}</b></div>
          <div><span style="color:#78716c;">Bobbin:</span> <b>${fd.bobbin_type}</b></div>
          <div><span style="color:#78716c;">Processing:</span> <b>${fd.processing_type}</b></div>
        </div>
        <div style="display:flex;gap:14px;margin-top:3px;">
          <div><span style="color:#78716c;">Warp:</span> <b>${fd.warp_count}/${fd.warp_quality}</b></div>
          <div><span style="color:#78716c;">Weft:</span> <b>${fd.weft_count}/${fd.weft_quality}</b></div>
          <div><span style="color:#78716c;">Kuri:</span> <b>${fd.kuri_count}/${fd.kuri_quality}</b></div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;border:1px solid #E7E5E4;">
        <thead><tr style="background:#F7F6F2;"><th style="padding:5px 8px;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;color:#78716c;border-bottom:2px solid #E7E5E4;"></th>${headerCells}</tr></thead>
        <tbody>
          ${normalRow("Warp Rate", (r) => r.warp_price_per_unit ?? "—")}
          ${normalRow("Warp Sizing", (r) => r.warp_sizing_per_unit ?? "—")}
          ${normalRow("Weft Rate", (r) => r.weft_price_per_unit ?? "—")}
          ${normalRow("Kuri Rate", (r) => r.kuri_price_per_unit ?? "—")}
          <tr><td colspan="${cols.length + 1}" style="border-bottom:1px solid #e7e5e4;padding:1px;"></td></tr>
          ${normalRow("Warp Total", (r) => (r.warp_total_cost * (r.size || 1))?.toFixed(2) || "—")}
          ${normalRow("Weft", (r) => (r.weft_cost * (r.size || 1))?.toFixed(2) || "—")}
          ${normalRow("Bobbin", (r) => (r.total_bobbin_cost * (r.size || 1))?.toFixed(2) || "—")}
          ${normalRow("Kuri", (r) => r.kuri_cost?.toFixed(2) || "—")}
          ${normalRow("Weaving", (r) => (r.weaving_wages * (r.size || 1))?.toFixed(2) || "—")}
          ${normalRow("Cost/Dhoti", (r) => r.cost_per_dhoti?.toFixed(2) || "—")}
          ${normalRow(`Weaver Profit (${cols[0]?.result_data?.weaver_profit_pct || 10}%)`, (r) => r.profit_amount?.toFixed(2) || "—")}
          ${hlRow("Greige Cost", (r) => r.greige_cost_per_dhoti?.toFixed(2) || "—")}
          ${normalRow("Processing", (r) => r.processing_cost?.toFixed(2) || "—")}
          ${normalRow("Folding", (r) => r.folding_cost?.toFixed(2) || "—")}
          ${normalRow("Trims", (r) => r.trims_cost?.toFixed(2) || "—")}
          ${normalRow("Final Cost", (r) => r.final_cost_per_dhoti?.toFixed(2) || "—")}
          ${normalRow(`Warehouse (${warehousePct}%)`, (r) => r.warehouse_cost?.toFixed(2) || "—")}
          ${hlRow("Landed Cost", (r) => r.landed_cost?.toFixed(2) || "—")}
          ${wspPrice ? normalRow("WSP", () => wspPrice) : ""}
          ${wspPrice ? normalRow("WSP MU / MD", (r) => { const m = getMargins(r.landed_cost, wspPrice); return m.mu + "% / " + m.md + "%"; }) : ""}
          ${mrpPrice ? normalRow("MRP", () => mrpPrice) : ""}
          ${mrpPrice && wspPrice ? normalRow("MRP MU / MD", () => { const m = getMargins(wspPrice, mrpPrice); return m.mu + "% / " + m.md + "%"; }) : ""}
        </tbody>
      </table>
      <div style="display:flex;gap:16px;margin-top:12px;">
        <div style="flex:1;border:1px solid #E7E5E4;border-radius:4px;padding:8px 12px;">
          <div style="font-size:9px;color:#78716c;margin-bottom:4px;">New WSP</div>
          <div style="height:20px;border-bottom:1px solid #ccc;"></div>
        </div>
        <div style="flex:1;border:1px solid #E7E5E4;border-radius:4px;padding:8px 12px;">
          <div style="font-size:9px;color:#78716c;margin-bottom:4px;">New MRP</div>
          <div style="height:20px;border-bottom:1px solid #ccc;"></div>
        </div>
      </div>
      <div style="text-align:center;font-size:9px;color:#a8a29e;margin-top:8px;">Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
    </div>`;

    const container = document.createElement("div");
    container.innerHTML = html;
    document.body.appendChild(container);
    html2pdf().set({
      margin: [6, 6, 6, 6],
      filename: `Supplier_Comparison_${design.design_name}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.92 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: cols.length > 4 ? "landscape" : "portrait" },
    }).from(container).save().then(() => document.body.removeChild(container));
    toast.success("PDF exported!");
  };

  if (initLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;

  // Results view
  if (results) {
    const { design, comparisons } = results;
    const rd = design.result_data || {};
    const cols = comparisons.filter((c) => !c.error);
    const wspPrice = rd.wsp_price;
    const mrpPrice = rd.mrp_price;

    const tableRows = [
      { label: "Product Cost", get: (r) => r.product_cost?.toFixed?.(2) },
      { label: "Warp Rate", get: (r) => r.warp_price_per_unit },
      { label: "Warp Sizing", get: (r) => r.warp_sizing_per_unit },
      { label: "Weft Rate", get: (r) => r.weft_price_per_unit },
      { label: "Kuri Rate", get: (r) => r.kuri_price_per_unit },
      { label: "sep" },
      { label: "Warp Total", get: (r) => r.warp_total_cost ? (r.warp_total_cost * (r.size || 1))?.toFixed(2) : undefined },
      { label: "Weft", get: (r) => r.weft_cost ? (r.weft_cost * (r.size || 1))?.toFixed(2) : undefined },
      { label: "Bobbin", get: (r) => r.total_bobbin_cost ? (r.total_bobbin_cost * (r.size || 1))?.toFixed(2) : undefined },
      { label: "Kuri", get: (r) => r.kuri_cost?.toFixed(2) },
      { label: "Weaving", get: (r) => r.weaving_wages ? (r.weaving_wages * (r.size || 1))?.toFixed(2) : undefined },
      { label: "Cost/Dhoti", get: (r) => r.cost_per_dhoti?.toFixed(2), bold: true },
      { label: `Weaver Profit`, get: (r) => r.profit_amount?.toFixed(2) },
      { label: "Greige Cost", get: (r) => r.greige_cost_per_dhoti?.toFixed(2), hl: true },
      { label: "Processing", get: (r) => r.processing_cost?.toFixed(2) },
      { label: "Folding", get: (r) => r.folding_cost?.toFixed(2) },
      { label: "Trims", get: (r) => r.trims_cost?.toFixed(2) },
      { label: "Final Cost", get: (r) => (r.final_cost || r.final_cost_per_dhoti)?.toFixed(2), bold: true },
      { label: `Warehouse (${warehousePct}%)`, get: (r) => r.warehouse_cost?.toFixed(2) },
      { label: "Landed Cost", get: (r) => r.landed_cost?.toFixed(2), hl: true },
    ];
    if (wspPrice) {
      tableRows.push({ label: "WSP", get: () => wspPrice, bold: true });
      tableRows.push({ label: "WSP MU / MD", get: (r) => { const m = getMargins(r.landed_cost, wspPrice); return `${m.mu}% / ${m.md}%`; } });
    }
    if (mrpPrice) {
      tableRows.push({ label: "MRP", get: () => mrpPrice, bold: true });
      if (wspPrice) tableRows.push({ label: "MRP MU / MD", get: () => { const m = getMargins(wspPrice, mrpPrice); return `${m.mu}% / ${m.md}%`; } });
    }

    return (
      <div className="space-y-4" data-testid="weaver-compare-results">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setResults(null)} data-testid="weaver-compare-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold text-stone-900">Supplier Comparison</h1>
              <p className="text-xs text-stone-500">{design.design_name} — {cols.length} supplier(s)</p>
            </div>
          </div>
          <Button data-testid="weaver-compare-pdf" className="bg-[#C2410C] hover:bg-[#9A3412] text-white" onClick={exportPdf}>
            <FileDown className="h-4 w-4 mr-2" /> Export PDF
          </Button>
        </div>

        <SpecsBlock design={design} />

        <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-stone-500"></th>
                {cols.map((c, i) => (
                  <th key={i} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-stone-500">
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((item, idx) =>
                item.label === "sep" ? (
                  <tr key={idx}><td colSpan={cols.length + 1} className="py-0"><Separator /></td></tr>
                ) : (
                  <tr key={idx} className={item.hl ? "bg-[#C2410C] text-white" : item.bold ? "font-semibold" : ""}>
                    <td className={`px-4 py-2 ${item.hl ? "font-bold" : "text-stone-600"}`}>{item.label}</td>
                    {cols.map((c, i) => (
                      <td key={i} className="px-4 py-2 text-right font-mono">
                        {item.get(c.data || {}) ?? "—"}
                      </td>
                    ))}
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        <div className="flex gap-4 max-w-md">
          <div className="flex-1">
            <label className="text-xs font-medium text-stone-500 mb-1 block">New WSP</label>
            <Input data-testid="new-wsp-input" placeholder="Enter new WSP" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-stone-500 mb-1 block">New MRP</label>
            <Input data-testid="new-mrp-input" placeholder="Enter new MRP" />
          </div>
        </div>
      </div>
    );
  }

  // Selection view
  return (
    <div className="space-y-6" data-testid="weaver-compare-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Supplier Comparison
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Compare the cost of a design across weavers and vendors.
        </p>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-5 max-w-lg">
        <div>
          <label className="text-sm font-medium text-stone-700 mb-1.5 block">Select Design</label>
          <Input
            data-testid="weaver-compare-design-search"
            placeholder="Search saved designs..."
            value={designSearch}
            onChange={(e) => setDesignSearch(e.target.value)}
            className="mb-2 h-10"
          />
          <div className="border border-stone-200 rounded-md max-h-48 overflow-y-auto bg-white">
            {designs.filter((d) => d.design_name.toLowerCase().includes(designSearch.toLowerCase())).length === 0 ? (
              <div className="px-3 py-3 text-sm text-stone-400 text-center">No designs found</div>
            ) : designs.filter((d) => d.design_name.toLowerCase().includes(designSearch.toLowerCase())).map((d) => (
              <div
                key={d.id}
                data-testid={`weaver-design-option-${d.id}`}
                className={`px-3 py-2 text-sm cursor-pointer border-b border-stone-100 last:border-0 transition-colors ${
                  selectedDesign === d.id ? "bg-orange-50 text-[#C2410C] font-medium" : "hover:bg-stone-50"
                }`}
                onClick={() => setSelectedDesign(d.id)}
              >
                {d.design_name}
                {d.result_data?.calc_mode === "fob" && <span className="ml-2 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-semibold">FOB</span>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-stone-700 mb-1.5 block">Select Suppliers</label>
          {suppliers.length === 0 ? (
            <p className="text-sm text-stone-400">No suppliers found. Add weavers or vendors first.</p>
          ) : (
            <WeaverMultiSelect weavers={suppliers} selected={selectedSuppliers} onChange={setSelectedSuppliers} />
          )}
        </div>

        <Button
          data-testid="weaver-compare-run"
          className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white h-11"
          disabled={!selectedDesign || selectedSuppliers.length === 0 || loading}
          onClick={runComparison}
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {loading ? "Comparing..." : "Compare Costs"}
        </Button>
      </div>
    </div>
  );
}
